import type { ThemeOptions } from '@mui/material'
import type { MuiThemeInput } from '@commerce/tokens'
import {
  YALO_FONT_STACK,
  YALO_RADIUS,
  YALO_SEMANTIC,
  YALO_SHADOW,
  YALO_TEXT,
  yaloAccent,
  type YaloTextStyle,
} from '@commerce/tokens'

/**
 * A design token to a CSS type ramp entry — **with units**.
 *
 * `@commerce/tokens` keeps these as plain numbers because React Native measures
 * in unitless density-independent pixels. CSS does not read them the same way,
 * and MUI passes them through untouched:
 *
 * - a unitless `lineHeight` is a **ratio**, so the design's `lineHeight: 24`
 *   means 24 × the font size. On `body1` that is 384px per line, and every
 *   element inheriting it — including the icon wrappers in the bottom nav —
 *   inflates to match. That shipped, and the first browser render is what
 *   caught it: jsdom computes no layout, so all 514 tests stayed green.
 * - a unitless `letterSpacing` is invalid CSS and is dropped silently, so the
 *   design's tracking simply never applied.
 */
const cssText = ({ fontSize, lineHeight, letterSpacing, fontWeight }: YaloTextStyle) => ({
  fontSize: `${fontSize}px`,
  lineHeight: `${lineHeight}px`,
  letterSpacing: `${letterSpacing}px`,
  ...(fontWeight !== undefined ? { fontWeight } : {}),
})

/**
 * The Yalo design system as a MUI theme.
 *
 * `@commerce/tokens` holds the values; this file is the only place they become
 * MUI's vocabulary. Keeping the mapping here rather than in the tokens package
 * is what lets the React Native shell read the same numbers without pulling in
 * MUI's types.
 *
 * Component defaults are set here on purpose. A screen that has to remember
 * `variant="outlined"` and `disableElevation` on every Button is a screen that
 * will eventually forget, and the design's flat, soft-shadowed look is a
 * property of the system, not of each call site.
 */
export const yaloMuiTheme = (
  storefront?: MuiThemeInput,
  accentColor?: string,
): ThemeOptions => {
  const accent = yaloAccent(accentColor)

  return {
    // The storefront's own values win, slot by slot. They are composed here
    // rather than deep-merged onto the finished theme with
    // `createTheme(a, b)`: later arguments to `createTheme` are merged over the
    // *result*, so a plain `spacing: 8` would replace the spacing **function**
    // MUI just built with the number 8, and everything that calls
    // `theme.spacing(2)` — Grid2's column spacing, for one — dies with
    // `_a.call is not a function`. It shipped once exactly that way.
    ...(storefront?.spacing !== undefined ? { spacing: storefront.spacing } : {}),
    palette: {
      mode: storefront?.palette.mode ?? 'light',
      primary: {
        main: accent.main,
        dark: accent.dark,
        light: accent.soft,
        contrastText: accent.contrast,
        ...storefront?.palette.primary,
      },
      secondary: { main: YALO_SEMANTIC.statusInfo, ...storefront?.palette.secondary },
      success: { main: YALO_SEMANTIC.statusSuccess, ...storefront?.palette.success },
      warning: { main: YALO_SEMANTIC.statusWarning, ...storefront?.palette.warning },
      error: { main: YALO_SEMANTIC.statusDanger, ...storefront?.palette.error },
      info: { main: YALO_SEMANTIC.statusInfo, ...storefront?.palette.info },
      background: {
        default: YALO_SEMANTIC.bgCanvas,
        paper: YALO_SEMANTIC.bgSurface,
        ...storefront?.palette.background,
      },
      text: {
        primary: YALO_SEMANTIC.fg1,
        secondary: YALO_SEMANTIC.fg2,
        disabled: YALO_SEMANTIC.fg4,
        ...storefront?.palette.text,
      },
      divider: YALO_SEMANTIC.borderSubtle,
    },

    shape: { borderRadius: storefront?.shape.borderRadius ?? YALO_RADIUS.md },

    typography: {
      fontFamily: storefront?.typography.fontFamily ?? YALO_FONT_STACK,
      ...(storefront?.typography.fontSize !== undefined
        ? { fontSize: storefront.typography.fontSize }
        : {}),
      h1: cssText(YALO_TEXT.h1),
      h2: cssText(YALO_TEXT.h2),
      h3: cssText(YALO_TEXT.h3),
      h4: cssText(YALO_TEXT.p2xl),
      h5: cssText(YALO_TEXT.pXl),
      h6: cssText(YALO_TEXT.pLg),
      subtitle1: cssText(YALO_TEXT.pMd),
      subtitle2: cssText(YALO_TEXT.pSm),
      body1: cssText(YALO_TEXT.pMd),
      body2: cssText(YALO_TEXT.pSm),
      caption: cssText(YALO_TEXT.pXs),
      // The eyebrow is a real role in this system — small, spaced, uppercase —
      // and `overline` is the slot MUI already has for it.
      overline: { ...cssText(YALO_TEXT.eyebrow), textTransform: 'uppercase' },
      button: { ...cssText(YALO_TEXT.pSm), fontWeight: 600, textTransform: 'none' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: YALO_SEMANTIC.bgCanvas },
          // The design never underlines a link; it colours it.
          a: { textDecoration: 'none', color: 'inherit' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: YALO_RADIUS.pill, paddingInline: 20, minHeight: 44 },
          // 44px is the minimum comfortable touch target, and every primary
          // action in this design is a full-width pill at the bottom of a
          // phone screen.
          sizeLarge: { minHeight: 52, fontSize: YALO_TEXT.pMd.fontSize },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: YALO_RADIUS.md } },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none', borderRadius: YALO_RADIUS.lg },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: YALO_RADIUS.lg,
            border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
            boxShadow: YALO_SHADOW.sm,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: YALO_RADIUS.pill, fontWeight: 600 },
          sizeSmall: { height: 26 },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: YALO_RADIUS.md, backgroundColor: YALO_SEMANTIC.bgSurface },
          notchedOutline: { borderColor: YALO_SEMANTIC.borderDefault },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: YALO_SEMANTIC.borderSubtle } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: YALO_RADIUS.md } },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: YALO_RADIUS.xl } },
      },
      MuiDrawer: {
        styleOverrides: {
          // Bottom sheets are the design's modal of choice on phone.
          paperAnchorBottom: {
            borderTopLeftRadius: YALO_RADIUS.xl,
            borderTopRightRadius: YALO_RADIUS.xl,
          },
        },
      },
    },
  }
}
